import * as ExcelJS from 'exceljs';

import { rowStartNumTemplateImg3 } from '@/constants/excel';
import type { ExcelDeviceList } from '@/types/InspectionArea.types';
import { addImageToSheet } from '@/utils/excelExport/excelExportUtils';
import { translateResultStatus } from '@/utils/constants';
import { useSelectedRoleStore } from '@/store/auth';

export const templateImg3ExportReportTable = async (
  data: ExcelDeviceList[],
  workbook: ExcelJS.Workbook,
  worksheet: ExcelJS.Worksheet
) => {
  const { isSbmRole } = useSelectedRoleStore.getState();
  
  // const worksheet = workbook.getWorksheet(idx + 6);

  if (worksheet) {
    for (let i = 0; i < data.length; i++) {
      const { device_info, item_list } = data[i];

      const {
        name,
        worker_list,
        start_date,
        end_date,
        device_num,
        location,
        detail_one,
        detail_two,
        memo,
        selected_image,
      } = device_info;

      const colOffset = 12 * i;
      // 각 점검항목 갯수가 다르기 때문에
      const rowOffset = item_list.length;

      const date = `${start_date.replaceAll('-', '.')}~${end_date
        .replaceAll('-', '.')
        .slice(5)}`;

      // 제목, 점검자, 점검일자, 설치위치
      worksheet.getCell(1, 1 + colOffset).value = `${name}${
        device_num ? `(${device_num})` : ''
      } 성능점검표 ${data.length > 1 ? `#${i + 1}` : ''}`;

      let rowBase = 0;
      if (isSbmRole()) {
        worksheet.getCell(3, 3 + colOffset).value = device_num;
        worksheet.getCell(3, 9 + colOffset).value = location;
        worksheet.getCell(4, 3 + colOffset).value = worker_list.join(', ');
        worksheet.getCell(4, 9 + colOffset).value = date;
        
        rowBase = 1;
      } else {
        worksheet.getCell(3, 3 + colOffset).value = worker_list.join(', ');
        worksheet.getCell(3, 7 + colOffset).value = date;
        worksheet.getCell(3, 11 + colOffset).value = location;
      }

      const dataInspectionArr =
        rowStartNumTemplateImg3[name as keyof typeof rowStartNumTemplateImg3]
          .dataInspectionArr;

      // 점검 항목
      item_list.forEach((item: any, idx: number) => {
        worksheet.getCell(5 + idx + rowBase, 10 + colOffset).value = `${idx + 1}. ${
          item.content
        }`;
        worksheet.getCell(5 + idx + rowBase, 11 + colOffset).value = isSbmRole() ? translateResultStatus(item.result) : item.result;
        
        if (isSbmRole() && dataInspectionArr?.[idx]) {
          const startRow = dataInspectionArr[idx] + 3;
          const targetValues = ['점검 기준', '점검 방법'];
          
          for (let h = startRow; h < startRow + 10; h++) {
            const cellValue = worksheet.getCell(h, 2 + colOffset).value;
            
            if (cellValue === '점검 기준') {
              worksheet.getCell(h, 12 + colOffset).value = translateResultStatus(item.result);
            }
            
            if (!targetValues.includes(cellValue as string)) {
              break;
            }
          }
        }
      });

      // 미흡사항, 조치필요사항
      worksheet.getCell(5 + rowOffset + rowBase, 3 + colOffset).value = `<미흡사항>\n${
        detail_one ?? ''
      }`;
      worksheet.getCell(
        8 + rowOffset + rowBase,
        3 + colOffset
      ).value = `<조치필요사항>\n${detail_two ?? ''}`;

      // 현황사진 — gom imageId + tl trước, sau đó addImage liên tiếp (không await giữa hai ảnh) để tránh mất ảnh đầu (xem 04_Bug_현황사진_Excel_Fix_Note.md)
      const imgExt = { width: 255, height: 200 };
      const hyeonsangPlacements: { imageId: number; tl: { col: number; row: number } }[] = [];
      for (let idx = 0; idx < (selected_image?.length ?? 0); idx++) {
        const imgUrl = selected_image?.[idx];
        if (!imgUrl) continue;
        const imageId = await addImageToSheet(imgUrl, workbook);
        if (imageId == null) continue;
        const tl =
          idx === 0
            ? { col: 0.1 + 2 + colOffset, row: 0.3 + 10 + rowOffset + rowBase }
            : { col: 0.1 + 7 + colOffset, row: 0.3 + 10 + rowOffset + rowBase };
        hyeonsangPlacements.push({ imageId, tl });
      }
      for (const { imageId, tl } of hyeonsangPlacements) {
        worksheet.addImage(imageId, { tl, ext: imgExt });
      }

      // 비고
      worksheet.getCell(21 + rowOffset + rowBase, 3 + colOffset).value = memo;

      // 하단 점검 결과 부분
      const templateConfig = rowStartNumTemplateImg3[name as keyof typeof rowStartNumTemplateImg3];
      const dataStartArr = isSbmRole() 
        ? (templateConfig.dataSbmStartArr ?? templateConfig.dataStartArr)
        : templateConfig.dataStartArr;

      // 측정값 위치 정보 부분
      const calculateInfoArr =
        rowStartNumTemplateImg3[name as keyof typeof rowStartNumTemplateImg3]
          ?.calculateInfoArr;

      let calculateCount = 0;

      for (let j = 0; j < item_list.length; j++) {
        const { image, result_text, result, value, image_captions } = item_list[j];

        // 측정값 넣는 부분
        if (value.length > 0 && calculateInfoArr) {
          const fixedRowPos = dataStartArr[j];
          const calculateInfo = calculateInfoArr[calculateCount];

          if (calculateInfo) {
            // calculateInfo의 key='장비번호'은 장비번호 정보
            if (calculateInfo.hasOwnProperty('장비번호')) {
              const [rowGap, col] = calculateInfo['장비번호'];

              worksheet.getCell(rowGap + fixedRowPos + rowBase, col + colOffset).value =
                device_num;
            }

            value.forEach((info) => {
              if (calculateInfo.hasOwnProperty(info.label)) {
                const [rowGap, col] = calculateInfo[info.label];

                worksheet.getCell(rowGap + fixedRowPos + rowBase, col + colOffset).value =
                  info.value;
              }
            });

            calculateCount++;
          }
        }

        worksheet.getCell(
          dataStartArr[j] + 8 + rowBase,
          1 + colOffset
        ).value = `사유: ${result_text}`;

        // 적합일 때, 사유 비어있으면 자동으로 '점검기준 충족' 넣어주기
        if (result === 'O' && result_text.length < 1) {
          worksheet.getCell(
            dataStartArr[j] + 8 + rowBase,
            1 + colOffset
          ).value = `사유: 점검기준 충족`;
        }

        if (result === '/') {
          for (let k = 0; k < 3; k++) {
            worksheet.getCell(dataStartArr[j] + rowBase, 4 * k + 1 + colOffset).value =
              '해당사항없음';
          }
        }

        for (let i = 0; i < image.length; i++) {
          if (i < 3) {
            const imageId = await addImageToSheet(image[i], workbook);
            if (!imageId) continue; // Skip nếu không load được image

            worksheet.addImage(imageId, {
              tl: {
                col: 0.1 + i * 4 + colOffset,
                row: 0.3 + dataStartArr[j] - 1 + rowBase,
              },
              ext: { width: isSbmRole() ? 180 : 203, height: isSbmRole() ? 145 : 167 },
            });

            if (isSbmRole() && image_captions && image_captions[i] && image_captions[i].trim()) {
              worksheet.getCell(dataStartArr[j] + 8, 1 + i * 4 + colOffset).value = image_captions[i];
            }
          }
        }
      }
    }
  }
};
